import qs from 'qs';
import { captureException, setContext, startSpan } from '@sentry/react';
import { useNetworkErrorStore } from '@lib/store';
import { AuthUtils, LocalStorageUtils } from '@shared/utils';
import { RestApiError } from '../utils';
import { HttpMethodEnum, type Headers } from '../models';

interface Options {
  queryParams?: object;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  responseType?: 'none' | 'json' | 'blob' | 'arraybuffer' | 'text';
}
interface _Request {
  method: HttpMethodEnum;
  url: string;
  data?: string | {};
  options?: Options;
}

export class Api {
  private static readonly baseUrl = process.env.API_PROXY!;
  private static readonly ignoredStatusErrors = [401];

  protected static get = <Response>(url: string, options?: Options): Promise<Response> =>
    this._wrapRequest(() => this._request<Response>({ method: HttpMethodEnum.GET, url: url + this._objectToQuery(options?.queryParams), options }));

  protected static post = <Response>(url: string, data?: object, options?: Options): Promise<Response> =>
    this._wrapRequest(() =>
      this._request<Response>({ method: HttpMethodEnum.POST, url: url + this._objectToQuery(options?.queryParams), data, options }),
    );

  protected static put = <Response>(url: string, data?: object, options?: Options): Promise<Response> =>
    this._wrapRequest(() =>
      this._request<Response>({ method: HttpMethodEnum.PUT, url: url + this._objectToQuery(options?.queryParams), data, options }),
    );

  protected static patch = <Response>(url: string, data?: object, options?: Options): Promise<Response> =>
    this._wrapRequest(() =>
      this._request<Response>({ method: HttpMethodEnum.PATCH, url: url + this._objectToQuery(options?.queryParams), data, options }),
    );

  protected static delete = <Response>(url: string, options?: Options): Promise<Response> =>
    this._wrapRequest(() =>
      this._request<Response>({ method: HttpMethodEnum.DELETE, url: url + this._objectToQuery(options?.queryParams), options }),
    );

  private static _objectToQuery = (obj?: object) => {
    if (!obj) return '';
    return qs.stringify(obj, { addQueryPrefix: true, indices: false, allowDots: true, skipNulls: true });
  };

  private static setNetworkError = (value: boolean) => useNetworkErrorStore.setState({ data: value });

  private static async _wrapRequest<Response>(handler: () => Promise<Response>) {
    try {
      const result = await handler();
      return result;
    } catch (error) {
      if (error instanceof TypeError) this.setNetworkError(true);
      if (RestApiError.isRestApiError(error) && this.ignoredStatusErrors.includes(error.status)) {
        error.message = '';
        return Promise.reject(error);
      }
      throw error;
    }
  }

  private static async _request<Response>(request: _Request): Promise<Response> {
    return await startSpan({ op: 'http.client', name: `${request.method} ${this.baseUrl + request.url}` }, async (span) => {
      const traceId = span.spanContext().traceId;
      const spanId = span.spanContext().spanId;

      const parsedURL = new URL(this.baseUrl + request.url, location.origin);
      span.setAttribute('http.request.method', request.method);
      span.setAttribute('server.address', parsedURL.hostname);
      span.setAttribute('server.port', parsedURL.port || undefined);

      const headers =
        request.data && request.data instanceof FormData
          ? {
              'Request-Id': `${traceId}-${spanId}`,
              ...(request.options?.headers || {}),
            }
          : ({
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'Request-Id': `${traceId}-${spanId}`,
              ...(request.options?.headers || {}),
            } as Headers);
      if (AuthUtils.authDataCached?.accessToken) {
        headers.Authorization = `Bearer ${AuthUtils.authDataCached.accessToken}`;
        headers.partnerId = (LocalStorageUtils.getSelectedPartnerId() || 1).toString();
      }

      const options = {
        method: request.method,
        headers,
        body: request.data instanceof FormData ? request.data : request.data ? JSON.stringify(request.data) : null,
        signal: request.options?.signal,
      };

      const response = await fetch(this.baseUrl + request.url, options);

      span.setAttribute('http.response.status_code', response.status);
      span.setAttribute('http.response_content_length', Number(response.headers.get('content-length')));

      if (response.ok) {
        const responseType = request.options?.responseType || 'json';
        let data;

        if (responseType === 'none') data = null;
        else if (responseType === 'json') data = await response.json();
        else if (responseType === 'arraybuffer') data = await response.arrayBuffer();
        else if (responseType === 'blob') data = await response.blob();
        else data = await response.text();

        return data as Response;
      } else {
        if (request.options?.signal?.aborted) return {} as Response;
        let data: Record<string, string> = {};
        if (!this.ignoredStatusErrors.includes(response.status)) data = await response.json();
        else data.message = 'Unauthorized';

        const error = new RestApiError(data.message || 'Unknown error', response.status, data, request.method, request.url);
        setContext('Request', {
          requestId: `${traceId}-${spanId}`,
          method: request.method,
          url: request.url,
          status: response.status,
          message: data.message || 'Unknown error',
        });
        // setUser({ id: AuthUtils.getUserId() });
        captureException(error);

        throw error;
      }
    });
  }
}
